/** @format */
import { NextRouter } from 'next/router';
import { parse, stringify } from 'qs';
import { ParsedUrlQuery, ParsedUrlQueryInput } from 'querystring';

import { convertKeyNames, toCamelCase } from '@toucan/client/global/axios';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface SearchParam {
    [key: string]:
        | string
        | string[]
        | number
        | boolean
        | SearchParam
        | SearchParam[]
        | undefined;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface NextRoute {
    pathname: string;
    query: ParsedUrlQuery;
    asPath: string;
}

/**
 * Get the route and search params from the next router object.
 *
 * @param {NextRouter} router the next router
 * @returns the route and search params
 */
export function getParams(router: NextRouter) {
    return {
        route: router.query,
        search: parse(router.asPath.split('?')[1]),
    };
}

/**
 * Get the masked 'as' path of the route without search params
 *
 * @param {NextRouter} router the next router
 * @returns the masked 'as' path of the route without search params
 */
export function getMaskedPath(router: NextRouter) {
    return router.asPath?.split('?')[0] ?? '';
}

export function getLocalizedPath(router: NextRouter) {
    const basePath = router.asPath?.split('?')[0] ?? '';

    // Always include locale prefix when locale is present
    if (router.locale) {
        const localePrefix = `/${router.locale}`;
        if (!basePath.startsWith(localePrefix)) {
            return `${localePrefix}${basePath}`;
        }
    }

    return basePath;
}

/**
 * Updates query string param(s) of the router
 *
 * @param {NextRouter} router the next router
 * @param {SearchParam} params A query param or array of query params to update
 * @param {boolean} pushHistory If the history should be pushed or replaced
 */
export function setSearchParams(
    router: NextRouter,
    params: SearchParam,
    pushHistory: boolean = true
) {
    // get our current route and search params
    const { route, search } = getParams(router);

    // merge params with search params
    const searchParams = { ...search, ...params };

    // merge search params with route params
    const routeParams = { ...route, ...searchParams };

    // get the search params in a url friendly format
    const searchParamsString = stringify(
        convertKeyNames(searchParams, toCamelCase),
        {
            encodeValuesOnly: true,
            arrayFormat: 'repeat',
        }
    );

    // define our router update method
    const routerMethod = pushHistory ? 'push' : 'replace';

    // Force locale preservation for all locales, including default
    const targetUrl = `${getLocalizedPath(router)}${
        searchParamsString.length > 0 ? `?${searchParamsString}` : ''
    }`;

    // update the router
    router[routerMethod](
        {
            pathname: router.pathname,
            query: routeParams as ParsedUrlQueryInput,
        },
        targetUrl,
        { scroll: false }
    );
}

export function setSearchParamsForSearchResults(
    router: NextRouter,
    params: SearchParam,
    pushHistory: boolean = true
) {
    // get our current route and search params
    const { route, search } = getParams(router);

    // merge params with search params
    const searchParams = { ...search, ...params };

    // merge search params with route params
    const routeParams = { ...route, ...searchParams };

    const searchParamsWithoutFilterQuery = {
        ...searchParams,
        filterQuery: undefined,
    };

    // get the search params in a url friendly format
    const searchParamsString = stringify(searchParamsWithoutFilterQuery, {
        encodeValuesOnly: true,
        arrayFormat: 'repeat',
    });

    // define our router update method
    const routerMethod = pushHistory ? 'push' : 'replace';

    // Force locale preservation for all locales, including default
    const targetUrl = `${getLocalizedPath(router)}${
        searchParamsString.length > 0 ? `?${searchParamsString}` : ''
    }`;

    // update the router
    router[routerMethod](
        {
            pathname: router.pathname,
            query: routeParams as ParsedUrlQueryInput,
        },
        targetUrl,
        { scroll: false }
    );
}

/**
 * Get the current route object from the router.
 *
 * @param {NextRouter} router the next router
 */
export function getCurrentRoute(router: NextRouter): NextRoute {
    return {
        pathname: router.pathname,
        query: router.query,
        asPath: router.asPath,
    };
}

const noIndexPaths = [
    '/product',
    '/search',
    '/join-confirmation',
    '/login',
    '/account',
    '/kitbag',
    '/checkout',
    '/checkout-login',
    '/quote-summary',
    '/quote-book-in',
    '/auth-link',
    '/password-reset',
    '/payment-link',
    '/spares-and-repairs',
    '/newly-listed',
    '/basket',
];

export function shouldRobotsIndexRoute(path: string = '') {
    return !noIndexPaths.some((p) => path.startsWith(p));
}
